import RoundedCard from "@cle/components/RoundedCard";
import { NON_TRANSFERABLE_LABEL } from "@cle/utils/injunctionReportNotices";

export type SelectableReportStepKey =
  | "courseCertificates"
  | "teaching"
  | "authorship"
  | "reductions"
  | "legalAid"
  | "dispensations";

export type ReportCategorySummary = {
  selectedLessons: number;
  submittedLessons: number;
  totalLessons: number;
  transferredFromPreviousLessons: number;
  transferredToNext: number;
  /** Lektioner over kategoriloftet, som hverken indberettes eller overføres. */
  droppedLessons: number;
};

export type ReportOverviewCategoryMeta = {
  label: string;
  /** Kategoriloft for det indberettede. Udeladt = ingen grænse, og så vises intet. */
  maxReported?: number;
  /** Styrer om bortfaldet forklares med "kan ikke overføres til næste periode". */
  canTransferLessons: boolean;
};

export type RoundedReportOverviewProps = {
  categorySummaries: Record<SelectableReportStepKey, ReportCategorySummary>;
  reportCategories: Record<SelectableReportStepKey, ReportOverviewCategoryMeta>;
  /** Forklaring under tabellen, fx hvorfor lektioner over lektionskravet bortfalder. */
  footnote?: string;
};

const RoundedReportOverview = ({
  categorySummaries,
  reportCategories,
  footnote,
}: RoundedReportOverviewProps) => {
  const rows = Object.entries(reportCategories).map(([key, category]) => {
    const categoryKey = key as SelectableReportStepKey;
    const summary = categorySummaries[categoryKey];

    return {
      label: category.label,
      maxReported: category.maxReported,
      canTransferLessons: category.canTransferLessons,
      total: summary.totalLessons,
      submitted: summary.submittedLessons,
      transferred: summary.transferredToNext,
      transferredFromPreviousLessons: summary.transferredFromPreviousLessons,
      dropped: summary.droppedLessons,
    };
  });
  const totals = rows.reduce(
    (summary, row) => ({
      total: summary.total + row.total,
      submitted: summary.submitted + row.submitted,
      transferred: summary.transferred + row.transferred,
    }),
    { total: 0, submitted: 0, transferred: 0 },
  );

  return (
    <RoundedCard
      bodyClassName="p-0"
      className=""
      description="Samlet overblik over valgte lektioner før indsendelse."
      header="Overblik over indberetning"
    >
      <div className="table-responsive">
        <table className="table align-middle mb-0 cle-report-detail__overview-table">
          <thead>
            <tr>
              <th scope="col">Kategori</th>
              <th scope="col" className="text-end">
                I alt
              </th>
              <th scope="col" className="text-end">
                Indberettes for denne periode
              </th>
              <th scope="col" className="text-end">
                Overføres til næste periode
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              return (
                <tr key={row.label}>
                  <th scope="row">
                    <span>
                      {row.label}{" "}
                      {row.maxReported !== undefined ? (
                        <span
                          style={{ fontSize: "0.8rem", fontWeight: "normal" }}
                          className=" tiny opacity-50"
                        >
                          (Max {row.maxReported} lektioner)
                        </span>
                      ) : null}
                    </span>
                    {row.canTransferLessons ? (
                      <span className="d-block text-muted tiny">
                        Overført fra sidste periode:{" "}
                        {row.transferredFromPreviousLessons}
                      </span>
                    ) : (
                      <span className="d-block text-muted tiny">
                        {NON_TRANSFERABLE_LABEL}
                      </span>
                    )}
                    {row.dropped ? (
                      <span className="d-block text-muted tiny">
                        {row.dropped} lektioner over maksimum bortfalder
                      </span>
                    ) : null}
                  </th>
                  <td className="text-end">{row.total}</td>
                  <td className="text-end">{row.submitted}</td>
                  <td className="text-end">{row.transferred}</td>
                </tr>
              );
            })}
            <tr className="cle-report-detail__overview-total-row">
              <th scope="row">I alt</th>
              <td className="text-end">{totals.total}</td>
              <td className="text-end">{totals.submitted}</td>
              <td className="text-end">{totals.transferred}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {footnote ? (
        <p className="text-muted small mb-0 px-3 py-3 border-top">{footnote}</p>
      ) : null}
    </RoundedCard>
  );
};

export default RoundedReportOverview;
